import { Controller } from "@hotwired/stimulus";
import * as http from "utils/http";
import * as security from "utils/security";
import { displayServerErrors } from "controllers/forms/errors";
import { FeeManager } from "./fee_manager";
import { debounce } from "utils/events";

interface ManualPayment {
  city: string;
  first_name: string;
  is_waive_fee?: boolean;
  last_name: string;
  payment_amount: number;
  state: string;
  street_address?: string;
  zip_code: string;
}

export default class extends Controller {
  static targets = [
    "city",
    "firstName",
    "lastName",
    "modalContainer",
    "paymentAmount",
    "state",
    "streetAddress",
    "submitButton",
    "validationErrors",
    "zipCode",
  ];
  static values = {
    allowedDomains: Array,
    feesUrl: String,
  };

  declare readonly allowedDomainsValue: string[];
  declare readonly cityTarget: HTMLInputElement;
  declare readonly feesUrlValue: string;
  declare readonly firstNameTarget: HTMLInputElement;
  declare readonly lastNameTarget: HTMLInputElement;
  declare readonly modalContainerTarget: HTMLElement;
  declare readonly paymentAmountTarget: HTMLInputElement;
  declare readonly stateTarget: HTMLInputElement;
  declare readonly streetAddressTarget: HTMLInputElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly validationErrorsTarget: HTMLSpanElement;
  declare readonly zipCodeTarget: HTMLInputElement;

  feeManager?: FeeManager;
  waiveFeeCheckbox?: HTMLInputElement;

  connect() {
    this.modalContainerTarget.classList.remove("hidden");
    this.submitButtonTarget.onclick = this.submitManualPayment.bind(this);
    this.waiveFeeCheckbox = document.querySelector<HTMLInputElement>('[name="waive_convenience_fee"]');
    if (this.waiveFeeCheckbox) {
      this.feeManager = new FeeManager(this.waiveFeeCheckbox, this.feesUrlValue);
      const debouncedFeeRefresh = debounce(this.handleFeeRefresh.bind(this), 300);
      this.paymentAmountTarget.addEventListener("change", debouncedFeeRefresh);
      this.stateTarget.addEventListener("change", debouncedFeeRefresh);
      this.zipCodeTarget.addEventListener("change", debouncedFeeRefresh);
    }
  }

  closeModal() {
    this.element.remove();
  }

  handlePayment(event) {
    this.closeModal();
  }

  private async submitManualPayment(event: MouseEvent) {
    event.preventDefault();
    this.validationErrorsTarget.innerHTML = "";
    const body = { manual_payment: this.formToParams() };
    const response = await http.httpPost(this.submitButtonTarget.dataset.paymentsManualPaymentPath, body);

    if (response.ok) {
      const data = await response.json();
      security.redirectToAllowedUrl(this.allowedDomainsValue, data.redirect_url);
    } else {
      const data = await response.json();
      displayServerErrors(data.error, this.validationErrorsTarget, this.fieldToInputMap, this.application);
    }
  }

  private formToParams(): ManualPayment {
    const params = {};

    Object.entries(this.fieldToInputMap).forEach(([field, input]) => {
      if (input instanceof HTMLInputElement && input.type === "checkbox") {
        params[field] = input.checked;
      } else {
        params[field] = input.value;
      }
    });

    return params as ManualPayment;
  }

  get fieldToInputMap(): Record<string, HTMLInputElement | HTMLSelectElement> {
    return Object.assign(
      {
        city: this.cityTarget,
        first_name: this.firstNameTarget,
        last_name: this.lastNameTarget,
        payment_amount: this.paymentAmountTarget,
        state: this.stateTarget,
        street_address: this.streetAddressTarget,
        zip_code: this.zipCodeTarget,
      },
      this.waiveFeeCheckbox ? { is_waive_fee: this.waiveFeeCheckbox } : {},
    );
  }

  private async handleFeeRefresh() {
    const manualPayment = this.formToParams();
    if (!manualPayment.payment_amount) {
      return;
    }
    this.feeManager.refreshFee({
      payment_amount: manualPayment.payment_amount,
      state: manualPayment.state,
    });
  }
}
